import { motion } from "framer-motion";
import { Check, Home as HomeIcon, Building2, Factory, MapPin, ShieldCheck, Wrench } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import { SITE } from "@/data/site";

const FOCUS = [
  { Icon: HomeIcon, t: "Residential electrical", d: "Wiring, repairs, and upgrades for homes." },
  { Icon: Building2, t: "Commercial electrical", d: "Offices, retail, and tenant spaces." },
  { Icon: Factory, t: "Industrial electrical", d: "Facilities, power, and equipment." },
  { Icon: ShieldCheck, t: "CCTV & security cameras", d: "Surveillance installs done right." },
  { Icon: MapPin, t: "Los Angeles County", d: "Serving 91702 and surrounding areas." },
  { Icon: Wrench, t: "Hands-on experience", d: "Project work handled by the contractor." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Kensmarc Electric"
        description="Kensmarc Electric is owned by electrical contractor Marcos Hernandez, providing residential, commercial, and industrial electrical plus CCTV installation across Los Angeles County."
        path="/about"
      />
      <PageHeader
        eyebrow="About"
        title="About Kensmarc Electric"
        subtitle="A licensed California C-10 electrical contractor serving Los Angeles County — residential, commercial, and industrial — plus CCTV and security camera installation."
      />

      <section className="section-pad">
        <div className="container-kx grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="eyebrow"><span className="h-px w-6 bg-amber-400" /> The contractor</span>
            <h2 className="mt-3 font-display text-3xl font-bold uppercase text-white sm:text-4xl">Marcos Hernandez</h2>
            <p className="mt-4 leading-relaxed text-neutral-400">
              Kensmarc Electric is owned and operated by {SITE.owner}, the electrical contractor and business
              representative behind every project. Marcos leads residential, commercial, and industrial
              electrical work throughout {SITE.county}.
            </p>
            <p className="mt-4 leading-relaxed text-neutral-400">
              As a licensed {SITE.license}, Kensmarc Electric focuses on hands-on, dependable work — and,
              because Marcos is an electrician first, the company also handles CCTV and security camera
              installation with clean, professional wiring.
            </p>
          </div>

          {/* Company credentials — no owner portrait per owner preference */}
          <div className="rounded-2xl border border-[#20202a] bg-[#0c0c11] p-8" data-testid="about-credentials">
            <span className="kx-badge"><ShieldCheck className="h-3.5 w-3.5" /> {SITE.credentials}</span>
            <h3 className="mt-5 font-display text-2xl font-bold uppercase text-white">Licensed. Insured. Local.</h3>
            <p className="mt-3 leading-relaxed text-neutral-400">
              Kensmarc Electric is a licensed {SITE.license} handling residential, commercial, and industrial
              electrical work — plus CCTV and security camera installation — across {SITE.county}.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                "Residential electrical",
                "Commercial electrical",
                "Industrial electrical",
                "Panel upgrades",
                "Lighting & electrical",
                "CCTV & security cameras",
              ].map((c) => (
                <li key={c} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                  <span className="text-sm text-neutral-200">{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-start gap-2 border-t border-[#1a1a22] pt-5 text-sm text-neutral-400">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> {SITE.serviceAreaLine}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-[#1a1a22] bg-[#0c0c11] py-16 lg:py-20">
        <div className="container-kx">
          <h2 className="font-display text-2xl font-bold uppercase text-white">What Kensmarc Electric focuses on</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FOCUS.map(({ Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.05 }}
                className="kx-card p-6"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-400/10 text-amber-400">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="mt-4 font-semibold text-white">{t}</p>
                <p className="mt-1 text-sm text-neutral-400">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Work with Kensmarc Electric" />
    </>
  );
}
